import { X } from "lucide-react";

import type { KindredAnnouncement } from "@/lib/announcements";
import { AppButton, AppButtonRow, AppCard, AppCardBody, AppDialogSurface, AppText } from "../../ui-foundation";

interface KindredAnnouncementSurfaceProps {
  announcement: KindredAnnouncement | null;
  open: boolean;
  onDismiss: () => void;
  onAction: () => void;
}

const KindredAnnouncementSurface = ({
  announcement,
  open,
  onDismiss,
  onAction,
}: KindredAnnouncementSurfaceProps) => {
  if (!announcement || !open) return null;

  const ctaLabel = announcement.cta_label?.trim() || (announcement.dismissible ? "Open" : "Got it");

  if (announcement.display_style === "banner") {
    return (
      <div className="mx-auto mb-4 w-full max-w-6xl">
        <AppCard>
          <AppCardBody>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0 flex-1">
                <AppText className="text-sm font-semibold text-foreground">{announcement.title}</AppText>
                <AppText className="mt-1 text-sm leading-6 text-muted-foreground">{announcement.body}</AppText>
              </div>
              <AppButtonRow className="shrink-0">
                {announcement.cta_url_or_route ? (
                  <AppButton tone="primary" size="sm" onPress={onAction}>
                    {ctaLabel}
                  </AppButton>
                ) : null}
                {announcement.dismissible ? (
                  <AppButton tone="ghost" size="sm" onPress={onDismiss} aria-label="Dismiss announcement">
                    <X size={16} />
                  </AppButton>
                ) : null}
              </AppButtonRow>
            </div>
          </AppCardBody>
        </AppCard>
      </div>
    );
  }

  return (
    <AppDialogSurface
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && announcement.dismissible) {
          onDismiss();
        }
      }}
      dismissible={announcement.dismissible}
      title={announcement.title}
      description={announcement.body}
      maxWidthClassName="max-w-md"
      footer={
        <>
          {announcement.dismissible ? (
            <AppButton tone="outline" onPress={onDismiss}>
              Later
            </AppButton>
          ) : null}
          <AppButton onPress={onAction}>
            {ctaLabel}
          </AppButton>
        </>
      }
    />
  );
};

export default KindredAnnouncementSurface;
